<template>
  <hel-module title="企业战情室" :class="scope.clsPrefix" v-loading="loading">
    <template slot="extra">
      <span class="card-tag chat-tag" @click.stop="toggleChatInput">
        <i class="el-icon-plus"></i>
      </span>
      <span class="card-tag" @click.stop="handleOpenList">全部</span>
    </template>
    <template v-if="displayCards.length > 0">
      <div class="cards-row">
        <div
          v-for="(card, idx) in displayCards"
          :key="idx"
          class="card-content"
          @click="handleOpenDetail(card)"
        >
          <!-- <div class="card-type-tag">{{ card.typeLabel }}</div> -->
          <div class="content-section">
            <div class="section-header">
              <span class="war-section-title">{{ card.title }}</span>
            </div>
            <div class="section-desc">{{ card.desc }}</div>
          </div>
          <div class="action-buttons">
            <button
              v-for="(btn, bidx) in card.tags"
              :key="bidx"
              class="btn-secondary"
            >
              {{ btn }}
            </button>
          </div>
          <div class="footer-btn">
            <span class="card-tag detail-tag" @click.stop="handleOpenDetail(card)">详情</span>
            <div class="btn-primary" @click.stop="handleSmartAnalysis(card)">智能分析</div>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="card-empty">暂无数据</div>

    <!-- 下拉 CosmoClawInput -->
    <transition name="chat-fade">
      <div v-if="chatVisible" class="chat-dropdown">
        <div class="chat-dropdown-inner">
          <div class="chat-dropdown-header">
            <span>向智能体提问</span>
            <i class="el-icon-close" @click="chatVisible = false"></i>
          </div>
          <CosmoClawInput ref="input" force-enable :before-send="handleDropdownSend" />
        </div>
      </div>
    </transition>
  </hel-module>
</template>

<script>
import HelModule from './common/module/index.vue'
import CosmoClawInput from '../../smart-workbench/components/CosmoClawInput.vue'
import { getJobReportPageApi } from '@/api/agentAuth'
import { parseAgentTags } from '@/store/modules/cosmoClaw'

export default {
  name: 'EnterpriseWarRoom',
  components: {
    HelModule,
    CosmoClawInput
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    },
    agentId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-enterprise-war-room'
      },
      businessItems: [],
      newsItems: [],
      chatVisible: false
    }
  },
  computed: {
    currentAgentId() {
      return this.$store.state.cosmoClaw.currentAgentId || this.agentId
    },
    displayCards() {
      const cards = []
      const biz = this.businessItems
      const news = this.newsItems
      // 优先 1 经营 + 1 资讯，其次 2 经营，最后 2 资讯
      if (biz.length > 0 && news.length > 0) {
        cards.push(this.buildCard(biz[0], '经营简报'))
        cards.push(this.buildCard(news[0], '资讯简报'))
      } else if (biz.length > 0) {
        cards.push(this.buildCard(biz[0], '经营简报'))
        if (biz.length > 1) cards.push(this.buildCard(biz[1], '经营简报'))
      } else if (news.length > 0) {
        cards.push(this.buildCard(news[0], '资讯简报'))
        if (news.length > 1) cards.push(this.buildCard(news[1], '资讯简报'))
      }
      return cards
    }
  },
  mounted() {
    this.fetchReports()
  },
  watch: {
    // 发送后自动关闭下拉
    chatVisible(val) {
      // 不再清除 currentSessionKey，避免干扰 handleSmartAnalysis 中的 sendMessage
      if (val) {
        this._msgCleared = false
      }
    },
    // createNewSession 先清空消息(N→0)，sendMessage 再加一条(0→1)
    '$store.state.cosmoClaw.messages'(msgs) {
      if (!this.chatVisible) return
      if (msgs.length === 0) {
        this._msgCleared = true
      } else if (this._msgCleared && msgs[msgs.length - 1]?.type === 'user') {
        this._msgCleared = false
        this.chatVisible = false
      }
    }
  },
  methods: {
    buildCard(item, typeLabel) {
      return {
        item,
        typeLabel,
        title: item.title || typeLabel,
        desc: item.content || item.summary || item.title || '',
        tags: item.tags ? item.tags.split(',').slice(0, 2) : []
      }
    },
    async fetchReports() {
      this.loading = true
      try {
        const [bizRes, newsRes] = await Promise.all([
          getJobReportPageApi({ agentId: this.agentId, category: 'internal', page: 1, size: 2 }),
          getJobReportPageApi({ agentId: this.agentId, category: 'external', page: 1, size: 2 })
        ])
        this.businessItems = (bizRes && bizRes.data && bizRes.data.records) ? bizRes.data.records : []
        this.newsItems = (newsRes && newsRes.data && newsRes.data.records) ? newsRes.data.records : []
      } catch (error) {
        console.error('获取战情室报告失败:', error)
        this.businessItems = []
        this.newsItems = []
      } finally {
        this.loading = false
      }
    },
    handleOpenDetail(card) {
      this.$emit('open-detail', {
        item: card.item,
        source: 'warRoom',
        tabType: card.typeLabel === '经营简报' ? 'business' : 'news'
      })
    },
    handleOpenList() {
      this.$emit('open-list', this.agentId)
    },
    handleSmartAnalysis(card) {
      // 确保当前数字人已设置，否则 sendMessage 会因 isHandshaked=false 被拦截
      const store = this.$store
      if (!store.state.cosmoClaw.currentAgentName) {
        const agentId = this.currentAgentId
        let resolvedName = ''
        let resolvedId = ''
        if (agentId) {
          const agentList = store.state.user.agentList || []
          const match = agentList.find((a) => String(a.id) === String(agentId))
          if (match) {
            const { internalName } = parseAgentTags(match.tags, match.name)
            if (internalName) {
              resolvedName = internalName
              resolvedId = String(match.id)
            }
          }
        }
        // 兜底：使用第一个数字人
        if (!resolvedName) {
          const agentList = store.state.user.agentList || []
          const firstDh = agentList.find((a) => {
            const { type } = parseAgentTags(a.tags, a.name)
            return type === 'digital-human'
          })
          if (firstDh) {
            const { internalName } = parseAgentTags(firstDh.tags, firstDh.name)
            resolvedName = internalName || 'ceo_shu_zi_ren'
            resolvedId = String(firstDh.id)
          } else {
            resolvedName = 'ceo_shu_zi_ren'
          }
        }
        store.commit('cosmoClaw/SET_CURRENT_AGENT_NAME', {
          agentName: resolvedName,
          agentId: resolvedId
        })
      }
      this.$emit('smart-analysis', {
        type: 'enterpriseWarRoom',
        content: card.desc || '请深度分析今日内部经营简报'
      })
    },
    toggleChatInput() {
      this.chatVisible = !this.chatVisible
    },
    // chat-dropdown 发送：不再本地直接发送，跳转到当前数字人/智能体的对话页进行发送
    handleDropdownSend(payload) {
      const content = ((payload && payload.content) || '').trim()
      if (!content) return false
      this.chatVisible = false
      const agentId =
        (this.$route.query && this.$route.query.agentId) ||
        this.currentAgentId ||
        ''
      const query = { agentId }
      if (content) query.sendAfter = content
      const nav = this.$router.push({ name: 'SmartEngineChat', query })
      if (nav && typeof nav.catch === 'function') nav.catch(() => {})
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: 'rvc-cosmoplat-workspace-enterprise-war-room';
.#{$class-prefix} {
  .cards-row {
    display: flex;
    gap: 10px;
  }
  .card-type-tag {
    font-size: 11px;
    color: #4a5ff3;
    font-weight: 500;
    margin-bottom: 6px;
  }
  .card-tag {
    font-size: 12px;
    color: #4a5ff3;
    font-weight: 400;
    cursor: pointer;
    padding: 2px 8px;
  }
  .detail-tag {
    margin-right: 10px;
  }
 .card-content {
   display: flex;
   flex-direction: column;
   padding: 16px;
   border-radius: 8px;
   height: 138px;
   box-sizing: border-box;
   background: #ffffffb3;
   box-shadow: 0 0 8px 0 #005ddd29;
   flex: 1;
   min-width: 0;
   max-width: 50%;
 }
  .card-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 138px;
    font-size: 12px;
    color: #999;
    background: #ffffffb3;
    box-shadow: 0 0 8px 0 #005ddd29;
    border-radius: 8px;
    box-sizing: border-box;
  }
  .content-section {
    flex: 1;
  }
  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 13px;
  }
 .war-section-title {
   font-size: 14px;
   font-weight: 600;
   color: #333333;
   font-family: PingFangSC-Medium;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   width: 100%;
 }
 .esection-title {
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    font-family: PingFangSC-Medium;
  }
  .section-desc {
    font-size: 12px;
    color: #999;
    font-family: PingFangSC-Regular;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .action-buttons {
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    // overflow: hidden;
    min-width: 0;
  }
  .btn-secondary {
    padding: 3px 8px;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #666666;
    font-size: 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: PingFangSC-Regular;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 50%;
  }
  .btn-primary {
    padding: 4px 14px;
    border: none;
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    font-family: PingFangSC-Regular;
    background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
    border-radius: 12px;
  }
}
.footer-btn {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
/* 提问按钮 */
.chat-tag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #4a5ff3 !important;
}
.chat-tag i {
  font-size: 13px;
}
/* 提问悬浮下拉框 */
.chat-dropdown {
  position: absolute;
  top: -15px;
  right: 55px;
  z-index: 1000;
  width: 460px;
  margin-top: 4px;
}
.chat-dropdown-inner {
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 16px;
  padding: 12px 14px 0;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.18);
}
.chat-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #333;
}
.chat-dropdown-header .el-icon-close {
  font-size: 15px;
  color: #999;
  cursor: pointer;
  padding: 2px;
}
.chat-dropdown-header .el-icon-close:hover {
  color: #333;
}
/* 过渡动画 */
.chat-fade-enter-active,
.chat-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.chat-fade-enter,
.chat-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
<style lang="scss">
/* ↓ 以下覆盖 CosmoClawInput 在下拉中的样式（不影响原来的）↓ */
.chat-dropdown-inner {
  .claw-input-wrapper {
    padding: 0 0 10px 0 !important;
  }
  .input-box {
    border: none !important;
    box-shadow: none !important;
    min-height: 90px;
    padding: 0 0 10px 0;
  }
  .input-box:focus-within {
    border: none !important;
    box-shadow: none !important;
  }
}
</style>
